import type { Feature, FeatureCollection, Point } from "geojson";
import { uiPreferences } from "../ui/preferences";

const MOCK_ROOT = "/mock/ais";
const TRACK_MOCK_MMSI = "255946000";

export interface AisBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

export interface AisVesselProperties {
  kind: "vessel";
  id: string;
  mmsi: string;
  timestamp: number;
  timestamp_iso: string;
  course_deg: number;
  course_source?: "cog" | "heading" | "positions" | "legacy" | "unavailable";
  speed_knots?: number | null;
  speed_source?: "sog" | "positions" | "unavailable";
  name?: string;
  ship_type?: number;
  length_metres?: number;
  width_metres?: number;
  sample_date?: string;
  day_offset?: number;
  offset_seconds?: number;
}

export type AisPointFeature = Feature<Point, AisVesselProperties>;

export interface AisPointCollection
  extends FeatureCollection<Point, AisVesselProperties> {
  generated_at: number;
  generated_at_iso: string;
  count: number;
  truncated: boolean;
  next_cursor: string | null;
  time_of_day?: string;
  timezone?: string;
  days_requested?: number;
  sample_dates?: string[];
  day_counts?: Record<string, number>;
  window_minutes?: number;
  requested_start?: number;
  requested_end?: number;
  sample_seconds?: number;
  representation?: "sampled_tracks";
}

export type AisDataSource = "api" | "mock" | "unavailable";

function configuredBase(): string {
  return uiPreferences().aisServerUrl.trim().replace(/\/+$/, "");
}

function mockEnabled(): boolean {
  return (
    !configuredBase() &&
    (import.meta.env.DEV || String(import.meta.env.VITE_AIS_USE_MOCKS) === "true")
  );
}

export function aisDataSource(): AisDataSource {
  if (configuredBase()) return "api";
  return mockEnabled() ? "mock" : "unavailable";
}

export function aisDataSourceLabel(): string {
  switch (aisDataSource()) {
    case "api":
      return "Velocity AIS archive";
    case "mock":
      return "Saved development sample";
    default:
      return "AIS server not configured";
  }
}

export function aisDataAvailable(): boolean {
  return aisDataSource() !== "unavailable";
}

function finiteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

const COURSE_SOURCES = new Set(["cog", "heading", "positions", "legacy", "unavailable"]);
const SPEED_SOURCES = new Set(["sog", "positions", "unavailable"]);

function parseCollection(value: unknown): AisPointCollection {
  if (!value || typeof value !== "object") {
    throw new Error("The AIS server returned an invalid response.");
  }
  const candidate = value as Partial<AisPointCollection>;
  if (candidate.type !== "FeatureCollection" || !Array.isArray(candidate.features)) {
    throw new Error("The AIS server did not return a GeoJSON feature collection.");
  }
  for (const feature of candidate.features) {
    const coordinates = feature?.geometry?.coordinates;
    const properties = feature?.properties;
    if (
      feature?.type !== "Feature" ||
      feature.geometry?.type !== "Point" ||
      !Array.isArray(coordinates) ||
      coordinates.length < 2 ||
      !finiteNumber(coordinates[0]) ||
      !finiteNumber(coordinates[1]) ||
      !properties ||
      properties.kind !== "vessel" ||
      typeof properties.id !== "string" ||
      typeof properties.mmsi !== "string" ||
      !finiteNumber(properties.timestamp) ||
      !finiteNumber(properties.course_deg) ||
      (properties.speed_knots !== undefined &&
        properties.speed_knots !== null &&
        (!finiteNumber(properties.speed_knots) || properties.speed_knots < 0)) ||
      (properties.length_metres !== undefined && !finiteNumber(properties.length_metres)) ||
      (properties.width_metres !== undefined && !finiteNumber(properties.width_metres)) ||
      (properties.name !== undefined && typeof properties.name !== "string") ||
      (properties.ship_type !== undefined && !finiteNumber(properties.ship_type)) ||
      (properties.course_source !== undefined && !COURSE_SOURCES.has(properties.course_source)) ||
      (properties.speed_source !== undefined && !SPEED_SOURCES.has(properties.speed_source))
    ) {
      throw new Error("The AIS server returned an invalid vessel feature.");
    }
  }
  if (candidate.count !== candidate.features.length) {
    throw new Error("The AIS response count does not match its features.");
  }
  return candidate as AisPointCollection;
}

function queryBounds(parameters: URLSearchParams, bounds: AisBounds): void {
  parameters.set("west", String(Math.max(-180, Math.min(180, bounds.west))));
  parameters.set("south", String(Math.max(-90, Math.min(90, bounds.south))));
  parameters.set("east", String(Math.max(-180, Math.min(180, bounds.east))));
  parameters.set("north", String(Math.max(-90, Math.min(90, bounds.north))));
}

async function getCollection(path: string, signal: AbortSignal): Promise<AisPointCollection> {
  const url = new URL(path, location.origin);
  const headers = new Headers({ Accept: "application/geo+json, application/json" });
  if (url.username || url.password) {
    const decode = (value: string): string => {
      try {
        return decodeURIComponent(value);
      } catch {
        return value;
      }
    };
    const credentialBytes = new TextEncoder().encode(
      `${decode(url.username)}:${decode(url.password)}`,
    );
    let credentials = "";
    for (const byte of credentialBytes) credentials += String.fromCharCode(byte);
    headers.set("Authorization", `Basic ${btoa(credentials)}`);
    url.username = "";
    url.password = "";
  }
  const response = await fetch(url, {
    signal,
    credentials: "same-origin",
    headers,
  });
  if (response.status === 401) {
    throw new Error("The AIS service requires authentication.");
  }
  if (!response.ok) {
    throw new Error(`AIS data could not be loaded (${response.status}).`);
  }
  let payload: unknown;
  try {
    payload = JSON.parse(await response.text());
  } catch (error) {
    throw new Error("The AIS server returned a non-JSON response.", { cause: error });
  }
  return parseCollection(payload);
}

function apiPath(path: string, parameters: URLSearchParams): string {
  const root = new URL(`${configuredBase()}/`, location.origin);
  const url = new URL(path.replace(/^\//, ""), root);
  url.search = parameters.toString();
  return url.toString();
}

export async function fetchLatestAis(
  bounds: AisBounds,
  signal: AbortSignal,
): Promise<AisPointCollection> {
  if (aisDataSource() === "mock") {
    return getCollection(`${MOCK_ROOT}/vessels-latest.geojson`, signal);
  }
  if (!configuredBase()) throw new Error("AIS server is not configured.");
  const parameters = new URLSearchParams({ max_age_seconds: "900", limit: "5000" });
  queryBounds(parameters, bounds);
  return getCollection(apiPath("/v1/vessels/latest", parameters), signal);
}

export interface TypicalTrafficRequest {
  bounds: AisBounds;
  timeOfDay: string;
  timezone: string;
  days: number;
  windowMinutes: number;
}

function timeOfDayMinutes(value: string): number {
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
}

function mockTypicalRange(
  collection: AisPointCollection,
  request: TypicalTrafficRequest,
): AisPointCollection {
  const sourceTime = collection.time_of_day ?? "08:00";
  const shiftSeconds = (timeOfDayMinutes(request.timeOfDay) - timeOfDayMinutes(sourceTime)) * 60;
  const features = collection.features
    .filter((feature) => (feature.properties.day_offset ?? 1) <= request.days)
    .map((feature) => {
      const timestamp = feature.properties.timestamp + shiftSeconds;
      return {
        ...feature,
        properties: {
          ...feature.properties,
          timestamp,
          timestamp_iso: new Date(timestamp * 1000).toISOString(),
        },
      };
    });
  const dayCounts: Record<string, number> = {};
  for (const feature of features) {
    const sampleDate = feature.properties.sample_date;
    if (sampleDate) dayCounts[sampleDate] = (dayCounts[sampleDate] ?? 0) + 1;
  }
  return {
    ...collection,
    features,
    count: features.length,
    time_of_day: request.timeOfDay,
    timezone: request.timezone,
    days_requested: request.days,
    sample_dates: Object.keys(dayCounts).sort().reverse(),
    day_counts: dayCounts,
    window_minutes: request.windowMinutes,
  };
}

export async function fetchTypicalAis(
  request: TypicalTrafficRequest,
  signal: AbortSignal,
): Promise<AisPointCollection> {
  if (aisDataSource() === "mock") {
    return mockTypicalRange(
      await getCollection(`${MOCK_ROOT}/traffic-typical.geojson`, signal),
      request,
    );
  }
  if (!configuredBase()) throw new Error("AIS server is not configured.");
  const parameters = new URLSearchParams({
    time: request.timeOfDay,
    timezone: request.timezone,
    days: String(request.days),
    window_minutes: String(request.windowMinutes),
    sample_seconds: "600",
    limit: "10000",
  });
  queryBounds(parameters, request.bounds);
  return getCollection(apiPath("/v1/traffic/typical/tracks", parameters), signal);
}

function mockTypicalSnapshot(
  collection: AisPointCollection,
  request: TypicalTrafficRequest,
): AisPointCollection {
  const ranged = mockTypicalRange(collection, request);
  const nearest = new Map<string, AisPointFeature>();
  for (const feature of ranged.features) {
    const sampleDate = feature.properties.sample_date ?? localDateKey(feature.properties.timestamp);
    const key = `${sampleDate}:${feature.properties.id}`;
    const current = nearest.get(key);
    if (
      !current ||
      Math.abs(feature.properties.offset_seconds ?? 0) <
        Math.abs(current.properties.offset_seconds ?? 0)
    ) {
      nearest.set(key, feature);
    }
  }
  const features = [...nearest.values()];
  return { ...ranged, features, count: features.length };
}

function localDateKey(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/** One historical fix per vessel/day, used to temper live collision projections. */
export async function fetchTypicalAisSnapshot(
  request: TypicalTrafficRequest,
  signal: AbortSignal,
): Promise<AisPointCollection> {
  if (aisDataSource() === "mock") {
    return mockTypicalSnapshot(
      await getCollection(`${MOCK_ROOT}/traffic-typical.geojson`, signal),
      request,
    );
  }
  if (!configuredBase()) throw new Error("AIS server is not configured.");
  const parameters = new URLSearchParams({
    time: request.timeOfDay,
    timezone: request.timezone,
    days: String(request.days),
    window_minutes: String(request.windowMinutes),
    limit: "10000",
  });
  queryBounds(parameters, request.bounds);
  return getCollection(apiPath("/v1/traffic/typical", parameters), signal);
}

function emptyCollection(): AisPointCollection {
  const now = Date.now() / 1000;
  return {
    type: "FeatureCollection",
    features: [],
    count: 0,
    truncated: false,
    next_cursor: null,
    generated_at: now,
    generated_at_iso: new Date(now * 1000).toISOString(),
  };
}

function mockTrackRange(
  collection: AisPointCollection,
  days: number,
): AisPointCollection {
  const end = collection.features.reduce(
    (latest, feature) => Math.max(latest, feature.properties.timestamp),
    0,
  );
  if (!end) return collection;
  const start = end - Math.max(1, days) * 86_400;
  const features = collection.features.filter(
    (feature) => feature.properties.timestamp >= start,
  );
  return {
    ...collection,
    features,
    count: features.length,
    requested_start: start,
    requested_end: end,
  };
}

export async function fetchAisTrack(
  mmsi: string,
  days: number,
  signal: AbortSignal,
): Promise<AisPointCollection> {
  if (aisDataSource() === "mock") {
    if (mmsi !== TRACK_MOCK_MMSI) return emptyCollection();
    return mockTrackRange(
      await getCollection(`${MOCK_ROOT}/vessel-${TRACK_MOCK_MMSI}-track.geojson`, signal),
      days,
    );
  }
  if (!configuredBase()) throw new Error("AIS server is not configured.");
  const end = Date.now() / 1000;
  const parameters = new URLSearchParams({
    start: String(end - Math.max(1, days) * 86_400),
    end: String(end),
    sample_seconds: "120",
    limit: "10000",
  });
  return getCollection(
    apiPath(`/v1/vessels/${encodeURIComponent(mmsi)}/track`, parameters),
    signal,
  );
}
